export enum AllOrderTableColumnsKeys {
  PatientName = "patientName",
  PlanDetails = "planDetails",
  Authorization = "authorizationId",
  AuthRequired = "authRequired",
  Priority = "priority",
  Type = "type",
  DateOfService = "dateOfService",
  Location = "location",
  Status = "status",
  DocId = "docId",
  AssignedTo = "assignedTo",
}

export enum PatientOrdersTableColumnKeys {
  AUTH_ID = "authId",
  SUBMISSION_DATE = "submissionDate",
  SUBMITTED_BY = "submittedBy",
  PLAN_NAME = "planName",
  LOCATION = "location",
  PRIORITY = "priority",
  DATE_OF_SERVICE = "dateOfService",
  AUTH_REQUIRED = "authRequired",
  STATUS = "status",
  DOC_ID = "docId",
}

export enum PatientsTableColumnKeys {
  PatientName = "patientName",
  PatientId = "patientId", // MRN
  PlanId = "planId", // Member id
  PlanName = "planName",
  Dob = "dob",
  State = "state",
  PrimaryProvider = "primaryProvider",
}

export enum CptCodeWithDetailsKeys {
  JCodeID = "jCodeId",
  StartFrom = "startFrom",
  EndAt = "endAt",
  JCodes = "jCodes",
  Description = "description",
  PaRequired = "paRequired",
  Visits = "visits",
  RemainingVisits = "remainingVisits",
  VisitsAllowed = "visitsAllowed",
  Documents = "documents",
  DocWithUploadIcon = "docWithUploadIcon",
}

export enum NarAgentGridTableColumnKeys {
  Insurance = "insurance",
  Plan = "plan",
  JCode = "jCode",
  DrugName = "drugName",
  UpdatedAt = "updatedAt",
  AgentMode = "agentMode",
}

export enum DrugTableForExternalKeys {
  JCodeID = "jCodeId",
  StartFrom = "startFrom",
  EndAt = "endAt",
  JCodes = "jCodes",
  Description = "description",
  PaRequired = "paRequired",
  Visits = "visits",
  RemainingVisits = "remainingVisits",
  VisitsAllowed = "visitsAllowed",
}

export enum MemberTableColumnKeys {
  Name = "name",
  Status = "status",
  Email = "email",
  Role = "roleId",
  Options = "adminOptions",
}

export enum EligibilityErrorTableColumnKeys {
  PatientName = "patientName",
  PayerDetails = "payerDetails",
  Authorization = "authorizationId",
  AuthRequired = "authRequired",
  Priority = "priority",
  Type = "type",
  OrderDate = "orderDate",
  Location = "location",
  DocId = "docId",
  Error = "error",
}

export enum CmmOrderTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  DRUG = "drug",
  MEDICATION = "medication",
  PROVIDER_DETAILS = "providerDetails",
  PRIMARY_DIAGNOSES_DESCRIPTION = "primaryDiagnosesDescription",
  DATE_OF_BIRTH = "dateOfBirth",
  INSURANCE_STATE = "insuranceState",
  ACTION = "button",
  CMM_STATUS = "cmm_status",
  CMM_KEY = "cmmKey",
  FORM_NAME = "formName",
  ID = "id",
  DELETE_ICON = "cmmOrderDeleteIcon",
  PATIENT_ID = "patientId",
  ROW_DATA = "rowData",
  NO_DATA_FIELDS = "noDataFields",
  USERNAME = "username",
  BUTTON_WITH_THREE_DOTS = "buttonWithThreeDots",
  CMM_INPUT_VIEW_ICON = "cmmInputViewIcon",
  TYPE = "type",
  ADDITIONAL_NOTES = "additionalNotes",
  KEY_POC = "keyPoc",
  POC = "poc",
  ISSUE = "issue",
  PORTAL = "portal",
  NEEDS_REVIEW = "needsReview",
  HUMAN_REVIEW = "humanReview",
  SEQUENCE = "sequence",
  ORDER_NOTES_ICON = "orderNotesIcon",
}

export enum PharmaStpFileTableColumnKeys {
  BATCH = "batch",
  DATE_OF_WORK = "dow",
  PATIENT_MRN = "patient_mrn",
  PATIENT_NAME = "patientName",
  DOB = "dob",
  DRUG = "drug",
  INSURANCE_ID = "insuranceId",
  CMM_ID = "cmmId",
  SECOND_STP_STATUS = "secondStpStatus",
  RESPONSE_STATUS = "responseStpStatus",
  ROUTE_VIA = "routeVia",
  CMM = "cmm",
  TEXT_NOTE = "textNote",
  LETTER_UPLOAD = "letterUpload",
  BIN = "bin",
  ADD = "add",
  POC = "poc",
  SFTP = "sftp",
  CALL = "call",
  FILE_NAME = "fileName",
  ACTION = "action",
  IDENTIFIER = "identifier",
  CALLING_CASE_ID = "callingCaseId",
  CALLING_STATUS = "callingStatus",
  CALL_REQUIRED = "callRequired",
  CALL_REASON = "callReason",
  /** Follow-up date captured on the latest non-EPA summary. */
  NEXT_FOLLOW_UP = "nextFollowUp",
  /** Clock shortcut opening the non-EPA modal on its History tab. */
  CALLING_HISTORY_ICON = "callingHistoryIcon",
  /** Oasis `DotsWithPopup` column for extra row context (PA reason, comments, calling). */
  DETAILS_POPUP = "detailsPopup",
  /** Info icon that loads insurance/eligibility details on hover. */
  INSURANCE_INFO_ICON = "insuranceInfoIcon",
  SEND_TO_APPEAL = "sendToAppeal",
}

/** Column keys for the NYCBS External PA Requests table. */
export enum NycbsExternalTableColumnKeys {
  DATE_OF_WORK = "dow",
  PATIENT_NAME = "patientName",
  DOB = "dob",
  PHARMACY_TYPE = "pharmacyType",
  DRUG = "drug",
  CMM_ID = "cmmId",
  OUTCOME = "responseStpStatus",
  OUTCOME_CLASS = "paStatusBucket",
  SUMMARY = "cmm",
  LOG = "textNote",
  ORDER_STATUS_BADGE = "orderStatusBadge",
  IDENTIFIER = "identifier",
}

export enum PharmaPrimaryCareTableColumnKeys {
  DATE_OF_SERVICE = "dateOfService",
  CASE_NAME = "caseName",
  PATIENT_NAME = "patientName",
  PATIENT_MRN = "patientMrn",
  DATE_OF_BIRTH = "dateOfBirth",
  MEDICATION = "medication",
  PROVIDER_NAME = "providerName",
  CMM_KEY = "cmmKey",
  QA_STATUS = "qaStatus",
  CLINICAL_STATUS = "clinicalStatus",
  CASE_STATUS = "caseStatus",
  ACTION = "action",
  CLINICAL = "clinical",
  CMM = "cmm",
}

export enum NycbsPharmaOrderDataKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_BIRTH = "dateOfBirth",
  MEDICATION = "medication",
  PROVIDER_DETAILS = "providerDetails",
  DATE_OF_SERVICE = "dateOfService",
  ASSIGNEE = "assignee",
  KEY = "key",
  STATUS = "status",
  RE_RUN_ONCO_EMR = "reRunOncoEmr",
  RE_RUN_CMM = "reRunCmm",
  VIEW_CMM_INPUT = "viewCmmInput",
  REPORT_PRESCRIPTION_INACCURACY = "reportPrescriptionInaccuracy",
}

export enum DiagnosisCodeTableColumnKeys {
  ICD_10_CODE = "icd10Code",
  DESCRIPTION = "description",
  DRUG_NAME = "drugName",
  DIAGNOSIS_DATE = "diagnosisDate",
  LAST_VERIFIED_DATE = "lastVerifiedDate",
  IS_PRIMARY = "isPrimary",
  IS_SECONDARY = "isSecondary",
  CONFIDENCE_SCORE = "confidenceScore",
  SOURCE = "source",
  VIEW_THINKING = "viewThinking",
}

export enum EligibilityCheckTableColumnKeys {
  ID = "id",
  CREATED_AT = "createdAt",
  REPORT_DATE = "reportDate",
  STATUS = "status",
  DOWNLOAD_FILE = "downloadFile",
  VIEW_DETAILS = "viewDetails",
  TOTAL_ORDERS = "totalOrders",
}

export enum ProcessedOrdersTableColumnKeys {
  ID = "id",
  CREATED_AT = "createdAt",
  PATIENT_MRN = "patientMrn",
  DRUG_NAME = "drugName",
  STATUS = "status",
  RE_RUN_ONCO_EMR = "reRunOncoEmr",
  RE_RUN_CMM = "reRunCmm",
  VIEW_CMM_INPUT = "viewCmmInput",
  REPORT_PRESCRIPTION_INACCURACY = "reportPrescriptionInaccuracy",
}

export enum MedicalPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  STATUS = "status",
  SELECT = "select",
  PDF_EMBED_PAGE = "pdfEmbedPage",
  BO_VALUE = "boValue",
  AUTH_STATUS = "authStatus",
  LOCATION = "location",
  PROVIDER = "provider",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
}

export enum MyPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  PROVIDER = "provider",
  BO_VALUE = "boValue",
  RPA_STATUS = "rpaStatusForAlgolia",
  RPA_STATUS_TWO = "rpaStatusTwo",
  TAG_WITH_ICON = "tagWithIcon",
  ALERTS = "alerts",
}

export enum AuthorizedPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  PROVIDER = "provider",
  BO_VALUE = "boValue",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
}

export enum AuthOnFilePaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  PROVIDER = "provider",
  BO_VALUE = "boValue",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
}

export enum NarPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  PROVIDER = "provider",
  BO_VALUE = "boValue",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
}

export enum PendingPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
  BO_VALUE = "boValue",
  AUTH_STATUS = "authStatus",
  PROVIDER = "provider",
}

export enum DeniedPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
  BO_VALUE = "boValue",
  AUTH_STATUS = "authStatus",
  PROVIDER = "provider",
}

export enum NotToWorkPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
  BO_VALUE = "boValue",
  PROVIDER = "provider",
}

export enum NotToWorkFedoraPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
  BO_VALUE = "boValue",
  PROVIDER = "provider",
}
export enum NotToWorkStatCasePaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  PROVIDER = "provider",
  BO_VALUE = "boValue",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
}

export enum PodPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  PROVIDER = "provider",
  BO_VALUE = "boValue",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
}

export enum FinancialPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
}

export enum HoldPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
  BO_VALUE = "boValue",
  AUTH_STATUS = "authStatus",
  PROVIDER = "provider",
}

export enum GenericAuthStatusTableColumnKeys {
  PATIENT_DETAILS_WITH_LEFT_BORDER = "patientDetailsWithLeftBorder",
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  DATE_OF_WORK = "date_of_work",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  RPA_STATUS = "rpaStatusForAlgolia",
  RPA_STATUS_TWO = "rpaStatusTwo",
  BO_VALUE = "boValue",
  AUTH_STATUS = "authStatus",
  PROVIDER = "provider",
  SIDE_MODAL_ICON = "sideModalIcon",
  LAST_CHECKED = "last_Checked",
  HOLD_UNTIL = "holdUntil",
}

export enum PharmaPaDiffTableColumnKeys {
  PATIENT_DETAILS = "pharmaPaDiffPatientDetails",
  DATE_OF_SERVICE = "pharmaPaDiffDateOfService",
  MEDICATION = "pharmaPaDiffMedication",
  FORM_NAME = "pharmaPaDiffFormName",
  ROW_DATA = "pharmaPaDiffRowData",
  STATUS = "pharmaPaDiffStatus",
  CMM_KEY = "pharmaPaDiffCmmKey",
  VIEW_COMMENT = "pharmaPaDiffViewComment",
  ACTION = "pharmaPaDiffAction",
  ID = "id",
  COMMENT = "pharmaPaDiffComment",
  NO_DATA_FIELDS = "pharmaPaDiffNoDataFields",
  FORM_DIFF_FIELDS = "pharmaPaDiffFormDiffFields",
  QA_DIFF_FIELDS = "pharmaPaDiffQaDiffFields",
  STATUS_STRING = "pharmaPaDiffStatusString",
  COMMENT_STRING = "pharmaPaDiffCommentString",
}

export enum PharmaPaDiffFilterKeys {
  FormName = "form_name",
  DateOfService = "date_of_service",
  Status = "status",
  DrugName = "drug_name",
}

export enum OncologySubmissionTableColumnKeys {
  BATCH = "batch",
  INPUT = "input",
  DATE_OF_WORK = "dow",
  PATIENT_MRN = "patient_mrn",
  PATIENT_NAME = "patientName",
  ROUTE_VIA = "routeVia",
  DRUG = "drug",
  INSURANCE_ID = "insuranceId",
  CMM_ID = "cmmId",
  SECOND_STP_STATUS = "secondStpStatus",
  RESPONSE_STATUS = "responseStpStatus",
  CMM = "cmm",
  TEXT_NOTE = "textNote",
  BIN = "bin",
  ADD = "add",
  POC = "poc",
  SFTP = "sftp",
  FILE_NAME = "fileName",
  IDENTIFIER = "identifier",
  PA_STATUS_REASON = "paStatusReason",
  COMMENT = "comment",
  VIEW = "view",
  PA_STATUS_BUCKET = "paStatusBucket",
  SENT_TO_CLIENT = "sentToClient",
  SEND_TO_APPEAL = "sendToAppeal",
  APPEAL_INFO = "appealInfo",
  APPEAL_REASON = "appealReason",
  APPEAL_COMMENTS = "appealComments",
  APPEAL_TYPE = "appealType",
  APPEAL_STATUS = "appealStatus",
  DOB = "dobDisplay",
  CMM_STATUS = "cmmStatus",
  BIOSIMILAR_CASE = "biosimilarCase",
  CASE_APPEAL = "caseAppeal",
}

export enum CaseAppealsTableColumnKeys {
  DATE_OF_WORK = "dow",
  PATIENT_NAME = "patientName",
  DOB = "dobDisplay",
  DRUG = "drugDisplay",
  COVER_MY_MEDS_ID = "cmmId",
  SECOND_STP_STATUS = "secondStpStatus",
  RESPONSE_STATUS = "responseStatus",
  CASE_ID = "callingCaseId",
  POC = "poc",
  CASE_STATUS = "callingStatus",
  APPEAL_PROBABILITY = "appealProbability",
  /** `add` + `add-key-button` routes the click to `onAddKeyButtonClick`. */
  ACTION = "add",
  NEXT_FOLLOW_UP = "nextFollowUp",
  /** Clock shortcut opening the appeal history side panel. */
  HISTORY_ICON = "callingHistoryIcon",
}

export enum PharmfillPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
}

export enum QueryPaOrdersTableColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_SERVICE = "dateOfService",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  ASSIGNEE = "assignee",
  ROW_DATA = "rowData",
  STATE = "state",
  ID = "id",
  CREATED_AT = "created_at",
  AUTH_VERIFICATION = "authVerification",
  FINAL_STATUS = "finalStatus",
  PROVIDER = "provider",
  BO_VALUE = "boValue",
  RPA_STATUS = "rpaStatus",
  RPA_STATUS_TWO = "rpaStatusTwo",
}

export enum AuditTrialTableColumnKeys {
  TIMESTAMP = "timestamp",
  ACTIONS = "actions",
  EVENT_TYPE = "eventType",
  VIEW_INFO = "viewInfoForAuditTrial",
  ROW_DATA = "rowData",
  VIEW_SCREENSHOT = "viewScreenshot",
  VIEW_PDF = "viewPdf",
}

export enum PushToSftpModalTableColumnKeys {
  SELECT = "selected",
  FILENAME = "filename",
  ROW_COUNT = "rowCount",
  DUMPED_AT = "dumpedAt",
  RECEIVE_TIME = "receiveTime",
  DOWNLOAD = "download",
}

export enum SftpPushPendingRecordsTableColumnKeys {
  SELECT = "selected",
  FILENAME = "filename",
  ROW_COUNT = "rowCount",
  RESUBMIT_COUNT = "resubmitCount",
  PENDING_COUNT = "pendingCount",
  DUMPED_AT = "dumpedAt",
  RECEIVE_TIME = "receiveTime",
}

export enum SftpSummaryTableColumnKeys {
  FILENAME = "filename",
  APPROVED_CNT = "approvedCnt",
  NAR_CNT = "narCnt",
  DENIED_CNT = "deniedCnt",
  PENDING_CNT = "pendingCnt",
  NA_CNT = "naCnt",
  NO_CLINICAL_CNT = "noClinicalCnt",
  OTHER_CNT = "otherCnt",
  RESPONSE_TOTAL_CNT = "responseTotalCnt",
  TEXT_SUCCESS_CNT = "textSuccessCnt",
  TEXT_PROGRESS_CNT = "textProgressCnt",
  TEXT_ERROR_CNT = "textErrorCnt",
  TEXT_NA_CNT = "textNaCnt",
  TEXT_NOT_STARTED_CNT = "textNotStartedCnt",
  TEXT_TOTAL_CNT = "textTotalCnt",
}

export enum FormMatrixTableColumnKeys {
  FORM_NAME = "formName",
  ICD = "icd",
  DOSAGE = "dosage",
  DOSAGE_INSTR = "dosageInstr",
  EV = "ev",
  DAW = "daw",
  REVIEW_NEEDED = "reviewNeeded",
  DELETE = "formMatrixDelete",
}

export enum ConditionListTableColumnKeys {
  JSON_PARAM_NAME = "jsonParamName",
  FIELD_NAME = "fieldName",
  SHOW_ON_CONDITION_LIST = "showOnConditionList",
  DATA_TYPE = "dataType",
  OPTIONS = "options",
}

export enum ProviderOptionsTableColumnKeys {
  NAME = "name",
  SHOW_ON_LIST = "showOnList",
  DELETE = "providerOptionDelete",
}

export enum CmmInputUniqueValuesTableColumnKeys {
  COLUMN_NAME = "columnName",
  FIELD_NAME = "fieldName",
  SHOW_ON_LIST = "showOnList",
  DATA_TYPE = "dataType",
  TOP_VALUES = "topValues",
}
